import {
  Component, h, Host, Element,
  State,
  Listen
} from '@stencil/core';
import { IRouteChangedDetail, IRouteMatches } from '../../../common';
import { appRouter } from '../../../global';

@Component({
  tag: 'app-root',
  styleUrl: 'component.css',
  shadow: false,
})
export class AppRoot {
  @Element() element: HTMLAppRootElement | undefined;
  @State() installPrompt: any;

  router = appRouter;
  @State() routeMatches: IRouteMatches | null = null;

  @Listen('beforeinstallprompt', { target: 'window' })
  onBeforeinstallprompt(e: Event) {
    e.preventDefault();

    this.installPrompt = e;
  }

  @Listen('swUpdate', { target: 'window' })
  async onSWUpdate() {
    const registration = await navigator.serviceWorker.getRegistration();

    registration?.waiting &&
      registration.waiting.postMessage({ type: "skipWaiting" });
  }

  @Listen('routing', { target: 'window' })
  async onRouting(e: CustomEvent<boolean>) {

    this.element?.ownerDocument.documentElement.classList.toggle('routing', e.detail)
  }

  @Listen('routeChanged', { target: 'window' })
  async onRouteChange(e: CustomEvent<IRouteChangedDetail>) {

    this.routeMatches = e.detail.matches;

  }

  async install() {

    this.installPrompt.prompt();

    const { outcome } = await this.installPrompt.userChoice;

    console.log('install outcome', outcome);
    this.installPrompt = null;
  }

  async componentWillLoad() {

    await appRouter.init();
  }

  async componentWillRender() {

    const themeColorElement = document.head.querySelector<HTMLMetaElement>('[name="theme-color"]')!;
    themeColorElement.content = getComputedStyle(document.documentElement).getPropertyValue('--background-color');

    const metaData = appRouter.renderFirst(
      {
        home: () => ({
          "og:title": "A Developers Blog",
          "og:description": "I'm Anthony. I design and write websites, PWAs, APIs, and services in .NET and TypeScript",
          "og:image": "/assets/developer.webp",
          "og:image:alt": "AI Image. Old and new architecture in harmony"
        }),
        azureFunctionsDurableTasks: () => ({
          "og:title": "Azure Functions Durable Tasks",
          "og:description": "Durable Tasks are orchestrations and activities for complex operations and can be used with Azure Functions",
          "og:image": "/assets/conductor-cat.webp",
          "og:image:alt": "AI Image. a cat in a top hat conducting an orchestra of cats"
        }),
        dataIsKey: () => ({
          "og:title": "Data Is Key",
          "og:description": " Given that, you need to take collection and storage of data very seriously, as it can give you the ability to make the best choices on the direction you want to go next",
          "og:image": "/assets/future-castle.webp",
          "og:image:alt": "AI Image. Shows a castle in the future with square turrets, modern upgrades with glass, neon and metal pipework. Cloudy dramatic sky with an airship and a distant rocket going into space"
        }),
        expertAssignment: () => ({
          "og:title": "Expert Assignment",
          "og:description": "Building a web app for delivering assignments to Domain Experts",
          "og:image": "/assets/spy-master.webp",
          "og:image:alt": "AI Image. A Spy Master handing out assignments."
        }),
        lookMaNoHands: () => ({
          "og:title": "Look Ma No Hands!",
          "og:description": "With Web APIs available now, you can do a lot without a framework",
          "og:image": "/assets/cat-on-bike.webp",
          "og:image:alt": "AI Image. A cyberpunk brown/gold subtly striped Siberian cat white under the chin riding a chopper bike, with goggles, mischevious grin, waving his paws in the air like he just don't care, with sparks, neon, LEDs and electric cables on the bike, cloudy dramatic sky"
        }),
        theDeveloperAndTheAi: () => ({
          "og:title": "The Developer And The AI",
          "og:description": "So, it's been a few weeks since I left my last contract, and while I am waiting for the Bat Signal, I am investigating Rust",
          "og:image": "/assets/robot.webp",
          "og:image:alt": "AI Image. A happy and slightly manic steampunk robot leaning in so its face is forward. Lots of neon, bulbs and LEDs. The robot has a metal body with gears, pipes, and wires. Its eyes are glowing with different colors and its mouth is wide open with a grin. The background is dark and smoky, with sparks and steam coming out of the robot's head"
        }),
        workingFromHome: () => ({
          "og:title": "Working From Home",
          "og:description": "this, from my blog 10 years ago, I have worked from home since then and all of this holds true, bar some of the tec",
          "og:image": "/assets/coding-cat.webp",
          "og:image:alt": "AI Image. A steam punk brown Siberian cat riding a chopper bike, with goggles, smiling and waving his paws in the air like he just don't care, with sparks, neon and electric cables on the bike, plenty of sky. The bike has a large engine, a skull-shaped headlight, and a flame-shaped exhaust. The background is a blue sky with clouds and birds. The image has a retro-futuristic style, with contrast, saturation, and glow effects. The image is also more moody, smokey, and has some neon tubes on the bike"
        }),
      },
      ({
        "og:title": null,
        "og:description": null,
        "og:image": null,
        "og:image:alt": null
      }),
    );

    Object.keys(metaData).forEach(property => {

      let metaTag = document.head.querySelector<HTMLMetaElement>(`[property="${property}"]`);
      if (metaData[property] == null) {
        metaTag?.remove();

      } else {
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('property', property);
          document.head.insertBefore(metaTag, themeColorElement);
        }

        metaTag.content = metaData[property];
      }
    });
  }

  render() {
    return <Host>
      <header class="page-header section">
        <div class="title content box">
          <h1 title="A .NET/Typescript Developer and Architects Blog">
            <a {...appRouter.href.home()} >Blog</a>
          </h1>
          {this.installPrompt &&
            <button class="space link" title="install application"
              onClick={() => this.install()}>
              install
            </button>
          }
        </div>
        <div class="links content box">
          <app-share class="right space" />
          <a class="space" aria-label="visit antix.co.uk"
            href="https://antix.co.uk" target="_blank" rel="noopener">
            <img src="../../assets/a.svg" width="24" height="24" title="blogk logo" />
          </a>
          <a class="space" aria-label="visit Anthony Johnston's LinkedIn profile"
            href="https://linkedin.com/in/antix" target="_blank" rel="noopener">
            <img src="../../assets/linkedin.svg" width="24" height="24" title="linkedin logo" />
          </a>
        </div>
      </header>

      <main class="page-body">
        <noscript class="content box">
          <small class="info" title="enable JavaScript for a better experience on this website">is better with JS on</small>
        </noscript>
        {appRouter.renderFirst(
          {
            home: () => <app-home class="fit cell" />,
            azureFunctionsDurableTasks: () => <section>
              <entry-azure-functions-durable-tasks class="fit cell" />

              <div class="body content box">
                <a {...appRouter.href.home()} class="right">&hellip; back</a>
              </div>
            </section>,
            dataIsKey: () => <section>
              <entry-data-is-key class="fit cell" />

              <div class="body content box">
                <a {...appRouter.href.home()} class="right">&hellip; back</a>
              </div>
            </section>,
            theDeveloperAndTheAi: () => <section>
              <entry-the-developer-and-the-ai class="fit cell" />

              <div class="body content box">
                <a {...appRouter.href.home()} class="right">&hellip; back</a>
              </div>
            </section>,
            lookMaNoHands: () => <section>
              <entry-look-ma-no-hands class="fit cell" />

              <div class="body content box">
                <a {...appRouter.href.home()} class="right">&hellip; back</a>
              </div>
            </section>,
            workingFromHome: () => <section>
              <entry-working-from-home class="fit cell" />

              <div class="body content box">
                <a {...appRouter.href.home()} class="right">&hellip; back</a>
              </div>
            </section>,
            expertAssignment: () => <section>
              <entry-expert-assignment class="fit cell" />

              <div class="body content box">
                <a {...appRouter.href.home()} class="right">&hellip; back</a>
              </div>
            </section>
          },
          <app-not-found class="fit cell" />
        )}
      </main>
    </Host>;
  }
}
